import React, { useState } from 'react';
import { X, Eye, EyeOff, User, MapPin, Car, CheckCircle2 } from 'lucide-react';

// ── Defined at module level — prevents remount on re-render ───────────────────
const PasswordInput = ({ value, onChange, show, onToggle, placeholder, hasError }) => (
  <div style={{ position: 'relative' }}>
    <input
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: '100%', padding: '0.6rem 3rem 0.6rem 0.9rem',
        border: `1.5px solid ${hasError ? '#ef4444' : '#d1d5db'}`,
        borderRadius: '0.6rem', fontSize: '1.05rem',
        outline: 'none', background: '#fff',
        boxSizing: 'border-box', fontFamily: 'inherit',
        transition: 'border-color 0.15s', color: '#111827'
      }}
      onFocus={e => { if (!hasError) e.target.style.borderColor = '#6366f1'; }}
      onBlur={e  => { if (!hasError) e.target.style.borderColor = '#d1d5db'; }}
    />
    <button
      type="button" onClick={onToggle}
      style={{
        position: 'absolute', right: '0.72rem', top: '50%',
        transform: 'translateY(-50%)', background: 'none',
        border: 'none', cursor: 'pointer', color: '#9ca3af',
        padding: 0, display: 'flex', lineHeight: 1
      }}
    >
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  </div>
);

const StudentSignupForm = ({ onClose, onSave, onSubmit, prefilledEmail }) => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '',
    email: prefilledEmail || '',
    countryCode: '+44', phoneNumber: '',
    password: '', confirmPassword: '',
    dateOfBirth: '',
    pickupAddress: '', pickupPostcode: '',
    dropoffAddress: '', dropoffPostcode: '',
    vehicleOption: 'Automatic',
    termsAccepted: false
  });

  const [errors, setErrors]                           = useState({});
  const [sameAsPickup, setSameAsPickup]               = useState(false);
  const [showPassword, setShowPassword]               = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const set = (field, value) => {
    setFormData(p => {
      const updated = { ...p, [field]: value };
      if (sameAsPickup) {
        if (field === 'pickupAddress')  updated.dropoffAddress  = value;
        if (field === 'pickupPostcode') updated.dropoffPostcode = value;
      }
      return updated;
    });
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
  };

  const handleSameAsPickup = (checked) => {
    setSameAsPickup(checked);
    if (checked) {
      setFormData(p => ({
        ...p,
        dropoffAddress:  p.pickupAddress,
        dropoffPostcode: p.pickupPostcode
      }));
      setErrors(p => {
        const e = { ...p };
        delete e.dropoffAddress;
        delete e.dropoffPostcode;
        return e;
      });
    }
  };

  const validate = () => {
    const e = {};
    if (!formData.firstName.trim())    e.firstName    = 'Required';
    if (!formData.lastName.trim())     e.lastName     = 'Required';
    if (!formData.email.trim())        e.email        = 'Required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Invalid email';
    if (!formData.phoneNumber.trim())  e.phoneNumber  = 'Required';
    else if (!/^\d{10}$/.test(formData.phoneNumber.trim()))   e.phoneNumber = 'Phone number must be exactly 10 digits';
    if (!formData.password)            e.password     = 'Required';
    else if (formData.password.length < 8) e.password = 'Min 8 characters';
    if (!formData.confirmPassword)     e.confirmPassword = 'Required';
    else if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!formData.dateOfBirth) {
      e.dateOfBirth = 'Required';
    } else {
      const today = new Date();
      const dob   = new Date(formData.dateOfBirth);
      const age   = today.getFullYear() - dob.getFullYear()
        - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
      if (age < 16) e.dateOfBirth = 'Must be at least 16 years old';
    }
    if (!formData.pickupAddress.trim())  e.pickupAddress  = 'Required';
    if (!formData.pickupPostcode.trim()) e.pickupPostcode = 'Required';
    if (!sameAsPickup) {
      if (!formData.dropoffAddress.trim())  e.dropoffAddress  = 'Required';
      if (!formData.dropoffPostcode.trim()) e.dropoffPostcode = 'Required';
    }
    if (!formData.vehicleOption) e.vehicleOption = 'Please select a vehicle option';
    if (!formData.termsAccepted) e.termsAccepted = 'You must accept the Terms and Conditions';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const { confirmPassword, countryCode, phoneNumber, ...rest } = formData;
    const phone = `${countryCode}${phoneNumber.trim()}`;
    const payload = {
      ...rest,
      phone,
      name: `${formData.firstName} ${formData.lastName}`,
      registrationDate: new Date().toISOString().split('T')[0],
      status: 'pending'
    };
    if (onSubmit) onSubmit(payload);
    else if (onSave) onSave(payload);
  };

  // ── Style helpers ────────────────────────────────────────────────────────────
  const inp = (field, extra = {}) => ({
    width: '100%', padding: '0.6rem 0.9rem',
    border: `1.5px solid ${errors[field] ? '#ef4444' : '#d1d5db'}`,
    borderRadius: '0.6rem', fontSize: '1.05rem',
    outline: 'none', background: '#fff',
    boxSizing: 'border-box', fontFamily: 'inherit',
    color: '#111827', transition: 'border-color 0.15s',
    ...extra
  });

  const focus = (field) => ({
    onFocus: e => { if (!errors[field]) e.target.style.borderColor = '#6366f1'; },
    onBlur:  e => { if (!errors[field]) e.target.style.borderColor = '#d1d5db'; }
  });

  const Label = ({ children }) => (
    <label style={{
      fontSize: '0.9rem', fontWeight: 600, color: '#374151',
      letterSpacing: '0.03em', textTransform: 'uppercase',
      marginTop: '0.5rem', marginBottom: '0.3rem', display: 'block'
    }}>
      {children}
    </label>
  );

  const Err = ({ field }) => errors[field]
    ? <span style={{ fontSize: '0.84rem', color: '#ef4444',
        marginTop: '0.2rem', display: 'block' }}>{errors[field]}</span>
    : null;

  const SectionHead = ({ icon: Icon, label }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.48rem',
      borderBottom: '1.5px solid #e5e7eb', paddingBottom: '0.42rem',
      marginBottom: '0.78rem'
    }}>
      <Icon size={16} color="#6366f1" />
      <span style={{ fontSize: '0.84rem', fontWeight: 700, color: '#6366f1',
        letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</span>
    </div>
  );

  const grid2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.72rem' };
  const col    = { display: 'flex', flexDirection: 'column' };
  const mb     = (n) => ({ marginBottom: n });

  const maxDOB = (() => {
    const d = new Date(); d.setFullYear(d.getFullYear() - 16);
    return d.toISOString().split('T')[0];
  })();

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      background: '#fff', borderRadius: '1.2rem',
      boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
      width: '100%', maxWidth: '672px',
      margin: '0 auto', overflow: 'hidden'
    }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
        padding: '1.32rem 1.68rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <p style={{ margin: 0, fontSize: '0.84rem', color: 'rgba(255,255,255,0.7)',
            letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600 }}>
            BipzApp
          </p>
          <h2 style={{ margin: '0.12rem 0 0', fontSize: '1.38rem',
            fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
            Student Registration
          </h2>
        </div>
        {onClose && (
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none',
            borderRadius: '0.6rem', cursor: 'pointer', color: '#fff',
            padding: '0.42rem', display: 'flex', alignItems: 'center'
          }}>
            <X size={22} />
          </button>
        )}
      </div>

      {/* ── Form ────────────────────────────────────────────────────────── */}
      <form onSubmit={handleSubmit} style={{ padding: '1.32rem 1.68rem 1.5rem' }}>

        {/* Personal Information */}
        <div style={mb('1.08rem')}>
          <SectionHead icon={User} label="Personal Information" />

          <div style={{ ...grid2, ...mb('0.72rem') }}>
            <div style={col}>
              <Label>First Name *</Label>
              <input style={inp('firstName')} value={formData.firstName}
                onChange={e => set('firstName', e.target.value)} {...focus('firstName')} />
              <Err field="firstName" />
            </div>
            <div style={col}>
              <Label>Last Name *</Label>
              <input style={inp('lastName')} value={formData.lastName}
                onChange={e => set('lastName', e.target.value)} {...focus('lastName')} />
              <Err field="lastName" />
            </div>
          </div>

          <div style={{ ...grid2, ...mb('0.72rem') }}>
            <div style={col}>
              <Label>Email *</Label>
              <input style={inp('email', prefilledEmail
                ? { background: '#f9fafb', cursor: 'not-allowed' } : {})}
                type="email" value={formData.email} disabled={!!prefilledEmail}
                onChange={e => set('email', e.target.value)} {...focus('email')} />
              <Err field="email" />
            </div>
            <div style={col}>
              <Label>Phone *</Label>
              <div style={{
                display: 'flex', borderRadius: '0.6rem', overflow: 'hidden',
                border: `1.5px solid ${errors.phoneNumber ? '#ef4444' : '#d1d5db'}`,
                transition: 'border-color 0.15s'
              }}
                onFocusCapture={e => { e.currentTarget.style.borderColor = errors.phoneNumber ? '#ef4444' : '#6366f1'; }}
                onBlurCapture={e  => { e.currentTarget.style.borderColor = errors.phoneNumber ? '#ef4444' : '#d1d5db'; }}
              >
                <select value={formData.countryCode}
                  onChange={e => set('countryCode', e.target.value)}
                  style={{
                    padding: '0.6rem 0.5rem', fontSize: '1.05rem',
                    border: 'none', borderRight: '1.5px solid #e5e7eb',
                    background: '#f9fafb', color: '#374151',
                    fontFamily: 'inherit', outline: 'none', cursor: 'pointer',
                    flexShrink: 0, appearance: 'none', minWidth: '72px',
                    textAlign: 'center', fontWeight: 600
                  }}>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+353">🇮🇪 +353</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+61">🇦🇺 +61</option>
                  <option value="+971">🇦🇪 +971</option>
                </select>
                <input type="tel" value={formData.phoneNumber}
                  onChange={e => set('phoneNumber', e.target.value.replace(/\D/g, ''))}
                  placeholder="7700 900000"
                  style={{
                    flex: 1, padding: '0.6rem 0.9rem', fontSize: '1.05rem',
                    border: 'none', outline: 'none', background: '#fff',
                    fontFamily: 'inherit', color: '#111827', minWidth: 0
                  }} />
              </div>
              <Err field="phoneNumber" />
            </div>
          </div>

          <div style={grid2}>
            <div style={col}>
              <Label>Password *</Label>
              <PasswordInput value={formData.password} show={showPassword}
                onChange={e => set('password', e.target.value)}
                onToggle={() => setShowPassword(p => !p)}
                placeholder="Min 8 characters" hasError={!!errors.password} />
              <Err field="password" />
            </div>
            <div style={col}>
              <Label>Confirm Password *</Label>
              <PasswordInput value={formData.confirmPassword} show={showConfirmPassword}
                onChange={e => set('confirmPassword', e.target.value)}
                onToggle={() => setShowConfirmPassword(p => !p)}
                hasError={!!errors.confirmPassword} />
              <Err field="confirmPassword" />
            </div>
          </div>
        </div>

        {/* DOB + Vehicle */}
        <div style={mb('1.08rem')}>
          <SectionHead icon={Car} label="Date of Birth & Vehicle Preference" />
          <div style={grid2}>
            <div style={col}>
              <Label>Date of Birth *</Label>
              <input type="date" value={formData.dateOfBirth} max={maxDOB}
                style={inp('dateOfBirth')}
                onChange={e => set('dateOfBirth', e.target.value)} {...focus('dateOfBirth')} />
              <Err field="dateOfBirth" />
            </div>
            <div style={col}>
              <Label>Vehicle Preference *</Label>
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                {['Automatic', 'Manual'].map(opt => (
                  <button key={opt} type="button"
                    onClick={() => set('vehicleOption', opt)}
                    style={{
                      flex: 1, padding: '0.6rem 0.5rem',
                      borderRadius: '0.6rem', fontSize: '1rem', fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit',
                      border: `1.5px solid ${formData.vehicleOption === opt ? '#6366f1' : '#d1d5db'}`,
                      background: formData.vehicleOption === opt
                        ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' : '#fff',
                      color: formData.vehicleOption === opt ? '#fff' : '#374151',
                      transition: 'all 0.15s'
                    }}>
                    {opt}
                  </button>
                ))}
              </div>
              <Err field="vehicleOption" />
            </div>
          </div>
        </div>

        {/* Pickup & Drop Off */}
        <div style={mb('1.08rem')}>
          {/* SectionHead with Same as Pickup toggle on the right */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1.5px solid #e5e7eb', paddingBottom: '0.42rem',
            marginBottom: '0.78rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.48rem' }}>
              <MapPin size={16} color="#6366f1" />
              <span style={{ fontSize: '0.84rem', fontWeight: 700, color: '#6366f1',
                letterSpacing: '0.08em', textTransform: 'uppercase' }}>Pickup & Drop Off</span>
            </div>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              cursor: 'pointer', fontSize: '0.84rem', color: '#6366f1',
              fontWeight: 600, marginTop: 0
            }}>
              <input type="checkbox" checked={sameAsPickup}
                onChange={e => handleSameAsPickup(e.target.checked)}
                style={{ accentColor: '#6366f1', width: '15px', height: '15px', cursor: 'pointer' }} />
              Same as Pickup
            </label>
          </div>
          <div style={grid2}>
            {/* Pickup */}
            <div style={col}>
              <div style={{ ...col, ...mb('0.72rem') }}>
                <Label>Pickup Address *</Label>
                <input style={inp('pickupAddress')} value={formData.pickupAddress}
                  onChange={e => set('pickupAddress', e.target.value)} {...focus('pickupAddress')} />
                <Err field="pickupAddress" />
              </div>
              <div style={col}>
                <Label>Pickup Postcode *</Label>
                <input style={inp('pickupPostcode')} value={formData.pickupPostcode}
                  placeholder="BD1 1AA"
                  onChange={e => set('pickupPostcode', e.target.value)} {...focus('pickupPostcode')} />
                <Err field="pickupPostcode" />
              </div>
            </div>

            {/* Drop Off */}
            <div style={col}>
              {/* Same as pickup toggle */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                marginBottom: '0.5rem', cursor: 'pointer',
                fontSize: '0.84rem', color: '#6366f1', fontWeight: 600
              }}>
                <input type="checkbox" checked={sameAsPickup}
                  onChange={e => handleSameAsPickup(e.target.checked)}
                  style={{ accentColor: '#6366f1', width: '15px', height: '15px', cursor: 'pointer' }} />
                Same as Pickup
              </label>
              <div style={{ ...col, ...mb('0.72rem') }}>
                <Label>Drop Off Address *</Label>
                <input style={inp('dropoffAddress', sameAsPickup
                  ? { background: '#f9fafb', cursor: 'not-allowed' } : {})}
                  value={formData.dropoffAddress} disabled={sameAsPickup}
                  onChange={e => set('dropoffAddress', e.target.value)} {...focus('dropoffAddress')} />
                <Err field="dropoffAddress" />
              </div>
              <div style={col}>
                <Label>Drop Off Postcode *</Label>
                <input style={inp('dropoffPostcode', sameAsPickup
                  ? { background: '#f9fafb', cursor: 'not-allowed' } : {})}
                  value={formData.dropoffPostcode} disabled={sameAsPickup}
                  placeholder="BD1 1AA"
                  onChange={e => set('dropoffPostcode', e.target.value)} {...focus('dropoffPostcode')} />
                <Err field="dropoffPostcode" />
              </div>
            </div>
          </div>
        </div>

        {/* Terms + Submit */}
        <div style={{
          borderTop: '1.5px solid #e5e7eb', paddingTop: '1.02rem',
          display: 'flex', flexDirection: 'column', gap: '0.9rem'
        }}>
          <label style={{ display: 'flex', alignItems: 'flex-start',
            gap: '0.72rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={formData.termsAccepted}
              onChange={e => set('termsAccepted', e.target.checked)}
              style={{ marginTop: '2px', width: '18px', height: '18px',
                accentColor: '#6366f1', flexShrink: 0, cursor: 'pointer' }} />
            <span style={{ fontSize: '0.96rem', color: '#4b5563', lineHeight: 1.5 }}>
              I have read and agree to the{' '}
              <a href="https://bipzapp.com/terms" target="_blank" rel="noopener noreferrer"
                style={{ color: '#6366f1', fontWeight: 600 }}>
                Terms and Conditions
              </a>{' '}*
            </span>
          </label>
          {errors.termsAccepted && (
            <span style={{ fontSize: '0.84rem', color: '#ef4444', marginTop: '-0.48rem' }}>
              {errors.termsAccepted}
            </span>
          )}

          <div style={{ display: 'flex', gap: '0.72rem', justifyContent: 'flex-end' }}>
            {onClose && (
              <button type="button" onClick={onClose} style={{
                padding: '0.66rem 1.32rem', borderRadius: '0.6rem',
                border: '1.5px solid #d1d5db', background: '#fff',
                color: '#374151', fontSize: '1.05rem', fontWeight: 500,
                cursor: 'pointer', fontFamily: 'inherit'
              }}>
                Cancel
              </button>
            )}
            <button type="submit" style={{
              padding: '0.66rem 1.68rem', borderRadius: '0.6rem',
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              border: 'none', color: '#fff', fontSize: '1.05rem',
              fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: '0.48rem',
              boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
              transition: 'opacity 0.15s'
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <CheckCircle2 size={19} />
              Submit Registration
            </button>
          </div>
        </div>

      </form>
    </div>
  );
};

export default StudentSignupForm;